import React from "react";
import { Grid, Icon, List } from "semantic-ui-react";
import { useHistory } from "react-router-dom";
import { urlWithProtocol } from "helpers/globalFunctions";
import { definedRouteTypes } from "api/api";
import RichTextDisplay from "components/atoms/NewRichText/RichText/RichTextDisplay/RichTextDisplay";
import { IBusinessEntityDisplay } from "api/types/BusinessEntity";

import ContactDetailList from "../List/ContactDetailList";
import SimpleHeader, { definedHeaderTypes } from "../SimpleHeader/SimpleHeader";
import Typography from "../../atoms/Typography/Typography";
import { definedTypographyTypes } from "../../atoms/Typography/TypographyStyles";
import MediaButton, { definedMedia } from "../../atoms/Buttons/MediaButton";
import { PLACEHOLDER_SQUARE } from "../../../images/imageSources";
import { StyledOrganizationCard } from "../../../styles/CardStyles";
import PlaceHoldableImage from "../Images/PlaceholdableImage";

import { BUSINESS_ENTITY_CATEGORY_TITLE } from "constants/entityCategories";

interface Props {
  organization: IBusinessEntityDisplay;
}

const CompanyListCard: React.FC<Props> = ({ organization }) => {
  const history = useHistory();
  const {
    website,
    phone,
    media,
    imageUrl,
    slug,
    address,
    category,
    title,
    templateSite,
    hasTemplateSite,
    description,
    type,
  } = organization;
  const hasSite =
    type === definedRouteTypes.selfEmployed || templateSite || hasTemplateSite;
  const onOrgCardClick = () => {
    const route = `/${type}/${slug}`;

    if (hasSite) history.push(route);
  };

  const handleChildClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    // history push is used because there is anchor elemnts within this card
    <StyledOrganizationCard
      onClick={onOrgCardClick}
      role={hasSite ? "link" : undefined}
      id={slug}
      title={title}
    >
      <div id="image">
        <PlaceHoldableImage
          src={imageUrl}
          fallbackImage={PLACEHOLDER_SQUARE}
          thumbnail
          alt={title}
        />
      </div>
      <div id="content">
        <SimpleHeader type={definedHeaderTypes.emphasize} shrink>
          {title}
        </SimpleHeader>
        <Grid columns={2} stackable padded>
          <Grid.Column
            width="6"
            style={{
              paddingTop: 0,
              paddingBottom: 0,
              marginTop: 0,
              marginBottom: 0,
            }}
          >
            {category && (
              <List divided horizontal>
                {category && (
                  <List.Item>
                    <List.Content>
                      <List.Icon
                        name={
                          type === definedRouteTypes.companies
                            ? "building outline"
                            : "user outline"
                        }
                      />
                      <Typography type={definedTypographyTypes.caption}>
                        {BUSINESS_ENTITY_CATEGORY_TITLE[category] || category}
                      </Typography>
                    </List.Content>
                  </List.Item>
                )}
                {/* {countries && countries.length && (
                <List.Item>
                  <List.Content>
                    {countries.map((country) => (
                      <FlagLabel key={country} flag={country} />
                    ))}
                    {countries.length === 1 && (
                      <Typography type={definedTypographyTypes.caption}>
                        {t(`country:${countries[0]}`)}
                      </Typography>
                    )}
                  </List.Content>
                </List.Item>
              )} */}
              </List>
            )}
            <ContactDetailList phone={phone} address={address} />
          </Grid.Column>
          <Grid.Column
            width="10"
            style={{
              paddingTop: 0,
              paddingBottom: 0,
              marginTop: 0,
              marginBottom: 0,
            }}
          >
            <div style={{ marginTop: "-14px", marginBottom: "-14px" }}>
              <RichTextDisplay
                limit={280}
                textJson={description}
                id={`description-${slug}`}
              />
            </div>
          </Grid.Column>
        </Grid>
        <div id="media">
          {website && (
            <MediaButton
              media={definedMedia.website}
              id={`${definedMedia.website}-${slug}`}
              icon
              href={urlWithProtocol(website)}
              onClick={handleChildClick}
            />
          )}
          {media?.facebook && (
            <MediaButton
              media={definedMedia.facebook}
              id={`${definedMedia.facebook}-${slug}`}
              icon
              href={urlWithProtocol(media?.facebook)}
              onClick={handleChildClick}
            />
          )}
          {media?.twitter && (
            <MediaButton
              media={definedMedia.twitter}
              id={`${definedMedia.twitter}-${slug}`}
              icon
              href={urlWithProtocol(media?.twitter)}
              onClick={handleChildClick}
            />
          )}
          {media?.linkedin && (
            <MediaButton
              media={definedMedia.linkedin}
              id={`${definedMedia.linkedin}-${slug}`}
              icon
              href={urlWithProtocol(media?.linkedin)}
              onClick={handleChildClick}
            />
          )}
          {media?.instagram && (
            <MediaButton
              media={definedMedia.instagram}
              id={`${definedMedia.instagram}-${slug}`}
              icon
              href={media?.instagram}
              onClick={handleChildClick}
            />
          )}
        </div>
      </div>

      <div id="rightLink">
        <div id="red">
          <Icon name="arrow right" />
        </div>
      </div>
    </StyledOrganizationCard>
  );
};

export default CompanyListCard;
